<template>
  <div class="flex min-h-screen bg-gray-50">
    <Sidebar
      @open-create-modal="openCreateModal"
      @logout="logout"
    />

    <div class="lg:ml-64 p-4 sm:p-6 lg:p-8 w-full">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Request History</h1>
      <div v-if="requests.length === 0" class="text-center text-gray-500 py-4">
        No requests found
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="request in requests" :key="request._id" class="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <h3 class="text-lg font-semibold text-gray-800 mb-2">Request #{{ request._id.slice(-6).toUpperCase() }}</h3>
          <p class="text-md text-gray-600 mb-1"><span class="font-medium">Requester:</span> {{ request.requester?.name || 'Unknown User' }}</p>
          <p class="text-md text-gray-600 mb-1"><span class="font-medium">Amount:</span> {{ formatAmount(request.amount) }} RWF</p>
          <p class="text-md text-gray-600 mb-1"><span class="font-medium">Purpose:</span> {{ request.purpose }}</p>
          <p class="text-md mb-2"><span class="font-medium">Status:</span> <span :class="statusClass(request.status)">{{ request.status === 'reversed' ? 'Sent Back' : request.status }}</span></p>
          <router-link v-if="request.status === 'reversed' && request.requester && request.requester._id === user.id"
                       :to="`/request/${request._id}`"
                       class="inline-block mb-3 text-sm bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600 transition-colors">
            Edit &amp; Resubmit
          </router-link>
          <!-- Approval Path Visualization -->
          <ApprovalPathVisualization
            :amount="request.amount"
            :approvalPath="request.approvalPath"
            :status="request.status"
          />
          <!-- Comments Section -->
          <div v-if="hasComments(request)" class="mt-4 mb-4">
            <p class="text-gray-500 text-sm mb-2">Previous Comments</p>
            <div class="space-y-2">
              <!-- Finance Review Comments -->
              <div v-if="request.financeReview && request.financeReview.comments" 
                   class="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
                <div class="flex items-center mb-1">
                  <div class="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  <span class="text-xs font-medium text-blue-700">Finance Review</span>
                  <span class="text-xs text-blue-600 ml-auto">{{ formatDate(request.financeReview.reviewedAt) }}</span>
                </div>
                <p class="text-sm text-blue-800">{{ request.financeReview.comments }}</p>
                <div v-if="request.financeReview.reviewedBy" class="text-xs text-blue-600 mt-1">
                  By: {{ request.financeReview.reviewedBy.name }}
                </div>
              </div>
              <!-- MD Review Comments -->
              <div v-if="request.mdReview && request.mdReview.comments" 
                   class="bg-purple-50 border-l-4 border-purple-400 p-3 rounded-r">
                <div class="flex items-center mb-1">
                  <div class="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                  <span class="text-xs font-medium text-purple-700">Managing Director Review</span>
                  <span class="text-xs text-purple-600 ml-auto">{{ formatDate(request.mdReview.reviewedAt) }}</span>
                </div>
                <p class="text-sm text-purple-800">{{ request.mdReview.comments }}</p>
                <div v-if="request.mdReview.reviewedBy" class="text-xs text-purple-600 mt-1">
                  By: {{ request.mdReview.reviewedBy.name }}
                </div>
                <div v-if="request.mdReview.approvedAmount !== request.amount" class="text-xs text-purple-600 mt-1">
                  Approved Amount: RF {{ formatAmount(request.mdReview.approvedAmount) }}
                </div>
              </div>
            </div>
          </div>
          <!-- Attachments -->
          <div v-if="hasAttachments(request)" class="mb-4">
            <p class="text-gray-500 text-sm mb-2">Attachments</p>
            <div class="flex flex-wrap gap-2">
              <button v-if="request.attachments?.invoice" 
                      @click="downloadAttachment(request._id, 'invoice')"
                      class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors">
                📄 Invoice
              </button>
              <button v-if="request.attachments?.contract" 
                      @click="downloadAttachment(request._id, 'contract')"
                      class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors">
                📋 Contract
              </button>
              <button v-for="(doc, index) in request.attachments?.otherDocuments || []" 
                      :key="index"
                      @click="downloadAttachment(request._id, `other-${index}`)"
                      class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
                📎 Document {{ index + 1 }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Request Modal -->
      <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-4 sm:p-6 w-full max-w-2xl my-8">
          <CreateRequest @request-created="fetchRequestsAndCloseModal" @close="closeCreateModal" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CreateRequest from '../components/CreateRequest.vue';
import Sidebar from '../components/Sidebar.vue';
import ApprovalPathVisualization from '../components/ApprovalPathVisualization.vue';

export default {
  components: { CreateRequest, Sidebar, ApprovalPathVisualization },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      requests: [],
      showCreateModal: false,
    };
  },
  async mounted() {
    await this.fetchRequests();
  },
  methods: {
    async fetchRequests() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        if (['finance', 'managing_director', 'ceo'].includes(this.user.role)) {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/approved', { headers });
          this.requests = res.data;
        } else {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/my-requests', { headers });
          this.requests = res.data;
        }
      } catch (error) {
        console.error('Error fetching requests:', error);
      }
    },
    statusClass(status) {
      return {
        'text-green-600': status === 'approved',
        'text-red-600': status === 'rejected',
        'text-amber-600 font-semibold': status === 'reversed',
        'text-yellow-600': status === 'pending',
      };
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('en-RW').format(amount || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-RW', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    hasComments(request) {
      return (
        (request.financeReview && request.financeReview.comments) ||
        (request.mdReview && request.mdReview.comments)
      );
    },
    hasAttachments(request) {
      return request.attachments && (
        request.attachments.invoice || 
        request.attachments.contract || 
        (request.attachments.otherDocuments && request.attachments.otherDocuments.length > 0)
      );
    },
    async downloadAttachment(requestId, type) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/attachment/${requestId}/${type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}-${requestId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment');
      }
    },
    openCreateModal() {
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    fetchRequestsAndCloseModal() {
      this.fetchRequests();
      this.closeCreateModal();
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.stat-card {
  @apply bg-white rounded-2xl p-6 shadow-sm border border-gray-100 transition-all duration-300;
}

.stat-icon {
  @apply w-12 h-12 rounded-xl text-white flex items-center justify-center shadow-lg;
}

.action-btn {
  @apply flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-sm hover:shadow-md;
}
</style>
